

import { BrainTreeScripts} from "~/types/braintree.type";
import {TrolleyStatus} from "../../types/ecom/trolley/trolley-map";

export class LocalpayStrategy {
  private local_pay_instance: any | null = null;
  private data_collector: any | null = null;
  private customClientErrorHandler: ReturnType<typeof useNuxtApp>['$customClientErrorHandler'];

  constructor() {
    const { $customClientErrorHandler } = useNuxtApp();
    this.customClientErrorHandler = $customClientErrorHandler;
  }

  async loadBraintreeScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  async initializePayment(client, data_collector,trolleyDetail = null) {
    this.data_collector = data_collector;

    const braintree = (window as any).braintree;
    if(!braintree || !braintree.localPayment){
      await this.loadBraintreeScript(BrainTreeScripts.client);
      await this.loadBraintreeScript(BrainTreeScripts.localPay);
    }
    braintree.localPayment.create(
        {
          client: client,
          merchantAccountId: "TSNL-ECOM"
        },
        (err, localPaymentInstance) => {
          if (err) {
            console.error("Error initializing local payment:", err);
            // Handle the error appropriately
            return;
          }
          this.local_pay_instance = localPaymentInstance ;
          this.setupPaymentButton(localPaymentInstance );
          this.handleFallback(localPaymentInstance,trolleyDetail );
        }
    );
  }

  private setupPaymentButton(instance) {
    document
        .querySelector("#braintree-ideal-button")
        ?.addEventListener("click", async (event) => {
          event.preventDefault();
          useCheckoutStore().is_transaction_loading = true;
          await this.startPayment(instance);
        });
  }

  private async startPayment(instance) {
    const checkoutStore = useCheckoutStore();

    try {
      setInterval(async () => {
        const trolleyData = await useTrolleyStore().getTrolleyById();
        if (trolleyData && trolleyData.status && trolleyData.order_id && trolleyData.status === TrolleyStatus.Ordered) {
          const localePath = useLocalePath();
          instance.closeWindow();
          window.location = localePath(`/checkout/order-confirmation/${trolleyData.order_id}`);
        }
      }, 15000)
      let paymentOptions = await this.getPaymentOptions();
      let payload = await instance.startPayment(paymentOptions);
      checkoutStore.resetError();
      checkoutStore.is_ideal_loading = true;

      checkoutStore.createTransaction(payload.nonce, "braintree-ideal");
    } catch (err) {
      this.errorHandler(err);
      return err;
    }
  }

  private async getPaymentOptions(): Promise<any> {
    const trolleyStore = useTrolleyStore();
    const checkoutStore = useCheckoutStore();
    const authStore = useAuthStore();

    let user = authStore.user ?? authStore.guest;
    checkoutStore.is_ideal_loading = false;

    const successUrl = `${window.origin}/checkout/fallback?trolleyId=${trolleyStore.trolley_id}&originBrowser=${useRootStore().browser}&isGuest=${!!useRootStore().guest}`;

    const encodedUrl = encodeURIComponent(successUrl);

    return {
      paymentType: "ideal",
      amount:
          trolleyStore.trolley.order_response?.totals?.inc_vat.raw.toString()
              ? trolleyStore.trolley.order_response?.totals?.inc_vat.raw.toString()
              : "0",
      currencyCode: "EUR",
      givenName: user.first_name,
      surname: user.last_name,
      email: user.email,
      address: {
        countryCode: "NL",
      },
      fallback: {
        buttonText: useTranslation("checkout-clickOnBlueBox" , "Klik op dit blauwe vakje om je transactie te voltooien."),
        cancelButtonText: useTranslation("checkout-clickhereToReturn" , "Klik hier om terug te gaan naar de website"),
        cancelUrl:encodedUrl,
        url: encodedUrl
      },
      onPaymentStart: this.sendTransactionToServer,
    };
  }

  private async sendTransactionToServer(data, start) {
    const checkoutStore = useCheckoutStore();
    const trolleyStore = useTrolleyStore();
    const authStore = useAuthStore();

    try {
      const params = {
        payment_type: "braintree-ideal",
        payment_id: data.paymentId,
        order_total: trolleyStore.trolley.order_response?.totals?.inc_vat.raw.toString(),
        payment_method: "braintree",
        trolley_id: trolleyStore.trolley_id,
        billing_address_id: checkoutStore.billing_address?.id,
        payment_device_data: this.data_collector?.deviceData,
      };

      if (!trolleyStore.trolley_line_items || !checkoutStore.selected_delivery_address) return;

      if (trolleyStore.trolley_line_items?.Delivery.length > 0 || trolleyStore.trolley_line_items?.Directship) {
        params.delivery_address_id = checkoutStore.selected_delivery_address.id;
      }
      if (trolleyStore.trolley_line_items?.Collection.length > 0) {
        params.collection_address_id = checkoutStore.selected_collection_address.address_id;
      }
      if (trolleyStore.trolley_line_items?.NextDayCollection.length > 0) {
        params.next_day_collection_address_id = checkoutStore.selected_collection_address.address_id;
      }

      const options = {
        method: "post",
        params,
      };

      await useAjaxEcom("/trolleys/checkout/braintree", options);

      start();
    } catch (err) {
      this.errorHandler(err);
      throw err;
    }
  }

  private errorHandler(err) {
    const trolleyStore = useTrolleyStore();
    const checkoutStore = useCheckoutStore();
    checkoutStore.payment_error.ideal_error = useTranslation('idealPaymentCanceled' , 'Idealbetaling geannuleerd');
    checkoutStore.is_transaction_loading = false;
    checkoutStore.is_ideal_loading = false;
    const params = {
      payment_type: "braintree-ideal",
      order_total: trolleyStore.trolley.order_response?.totals?.inc_vat.raw.toString(),
      payment_method: "braintree",
      trolley_id: trolleyStore.trolley_id,
      billing_address_id: checkoutStore.billing_address?.id,
    };

    let errorMeta = {...params , idealPaymentError: true}

    this.customClientErrorHandler(
        err,
        'high',
        'Ideal Service Page Error - trying to perform payment through Ideal',
        errorMeta
    );

    if (this.local_pay_instance) {
      this.local_pay_instance.closeWindow()
      this.local_pay_instance.teardown(() => {
        this.local_pay_instance = null; // Reset the instance after teardown
        checkoutStore.initializePaymentMethod(PaymentMethod.LocalPay);
      });
    }
  }

  private async handleFallback(instance,trolleyDetail = null) {

    if (instance.hasTokenizationParams()) {
      instance
          .tokenize()
          .then(function (payload) {
            useCheckoutStore().createTransaction(payload.nonce, "braintree-ideal",'braintree',trolleyDetail);
          })
          .catch(async function (error) {

            let errorMeta = {
              urlParams,
              btLpToken,
              fallbackError: true
            }

            this.customClientErrorHandler(
                error,
                'high',
                'Ideal Service Page Error - trying to perform payment through Ideal using handleFallback()',
                errorMeta
            );
          });
    }

    return;
  }
}